import { toast } from "sonner"

export type ApiError = {
  status: number
  statusText: string
  error?: string
  type?: string
  title?: string
  detail?: string
  message?: string
}

// better-auth（/api/auth/*）のエラーは英語の文言のため、認証の画面では理由として添えない
export const getErrorMessage = (error: unknown, fallback?: string): string => {
  const apiError = (error ?? {}) as Partial<ApiError>
  // be の defaultHook が返す固定文言は英語のため置き換える
  if (apiError.detail === "Validation failed") {
    return "入力内容に誤りがあります。"
  }
  if (apiError.detail) {
    return apiError.detail
  }
  if (apiError.error) {
    return apiError.error
  }
  if (apiError.title) {
    return apiError.title
  }
  if (apiError.message) {
    return apiError.message
  }
  return fallback || "不明なエラーが発生しました"
}

// instance 側でトースト済み、または表示不要な error か（共通のトーストを出さない）
export const isErrorToastHandled = (error: unknown): boolean => {
  const apiError = (error ?? {}) as Partial<ApiError> & { name?: string }
  return (
    apiError.status === 401 || apiError.type === "/unauthorized" || apiError.name === "AbortError"
  )
}

export const customInstance = async <T>(
  url: string,
  {
    method,
    params,
    body,
    signal,
    headers: customHeaders,
  }: {
    method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
    params?: never
    body?: string | FormData
    responseType?: string
    signal?: AbortSignal
    // eslint-disable-next-line no-undef
    headers?: RequestInit["headers"]
  }
): Promise<T> => {
  // const targetUrl = new URL(url, API_BASE_URL)
  const targetUrl = new URL(url, import.meta.env.VITE_API_URL || "http://localhost")

  // console.log('request date', new Date().toString())
  // console.log('request isServer', isServer)
  console.groupCollapsed(`🔄 ${method}: ${targetUrl.pathname}`)
  console.log("URL:", targetUrl.toString())
  console.log("Data:", body)
  console.log("Params:", params)
  console.groupEnd()

  /**
   * header設定
   */
  const headers = new Headers({
    "Content-Type": "application/json",
    Accept: "application/json",
    // 'Accept-Language': i18n.language,
    ...customHeaders,
  })

  // Content-Type が multipart/form-data の場合は削除
  // 手動でつけると boundary がなくなり、PHP側でエラーになる
  const contentType = headers.get("Content-Type")
  if (contentType?.startsWith("multipart/form-data")) {
    headers.delete("Content-Type")
  }

  /**
   * tokenのセット
   * TODO: tokenの取得
   */
  // const token = 'Bearer: token'
  // if (token) {
  //   headers.set('Authorization', `Bearer $token`)
  // }

  // GET以外の場合 /csrf-token からCSRFトークンの取得
  // X-Billsan-CSRF-Tokenに設定
  // if (method !== 'GET') {
  //   try {
  //     const csrfToken = await fetch(`$API_BASE_URL/api-go/csrf-token`).then(
  //       (res) => res.json(),
  //     )
  //     headers.set('x-billsan-csrf-token', get(csrfToken, 'csrf_token', ''))
  //   } catch (error) {
  //     console.error('Failed to fetch CSRF token:', error)
  //     throw error
  //   }
  // }

  /**
   * APIコール
   */
  try {
    const response = await fetch(targetUrl.toString(), {
      method,
      ...(body ? { body: body instanceof FormData ? body : body } : {}),
      headers,
      signal,
      credentials: "include",
    })

    console.groupCollapsed(`✅ Response: ${response.status}${response.statusText}`)
    console.log("Response:", response)
    console.log("Status:", response.status)
    console.log("Headers:", Object.fromEntries(response.headers.entries()))
    console.groupEnd()

    if (!response.ok) {
      const errorData: ApiError = {
        status: response.status,
        statusText: response.statusText,
      }

      try {
        const errorResponse = await response.json()
        if (errorResponse && typeof errorResponse === "object") {
          // エラーレスポンスがオブジェクトの場合はそのまま使用
          Object.assign(errorData, errorResponse)
        }
      } catch (e) {
        console.warn("Failed to parse error response as JSON:", e)
      }

      // ログイン失敗の 401 はセッション切れではないので、専用の文言を出して遷移しない
      if (response.status === 401 && url === "/api/auth/sign-in/email") {
        const message = "メールアドレスまたはパスワードが正しくありません。"
        toast.error(message)
        const error = new Error(message)
        Object.assign(error, errorData)
        throw error
      }

      // 401エラーの処理
      if (response.status === 401 || errorData.type === "/unauthorized") {
        const message = getErrorMessage(errorData, "セッションの有効期限が切れました。")
        toast.error(`${message} 再度ログインしてください。`)

        // 少し遅延を入れてからリダイレクト（トーストが表示されるため）
        setTimeout(() => {
          if (window.location.pathname.startsWith("/signin")) {
            return
          }
          const currentPath = window.location.pathname + window.location.search
          window.location.href = `/signin?redirect=${encodeURIComponent(currentPath)}`
        }, 1500)

        // エラーをスローして処理を中断
        const error = new Error(message)
        Object.assign(error, errorData)
        throw error
      }

      // 403エラーの処理（表示は onError か共通のトーストが担当）
      if (response.status === 403 || errorData.type === "/forbidden") {
        const message = getErrorMessage(errorData, "この操作を実行する権限がありません。")
        const error = new Error(message)
        Object.assign(error, errorData)
        throw error
      }

      // 404エラーの処理
      if (response.status === 404 || errorData.type === "/not-found") {
        const message = getErrorMessage(errorData, "リソースが見つかりません。")
        // 404は画面側で処理することが多いのでtoastは出さない
        const error = new Error(message)
        Object.assign(error, errorData)
        throw error
      }

      // 500番台エラーの処理
      if (response.status >= 500) {
        const message = getErrorMessage(errorData, "サーバーエラーが発生しました。")
        const error = new Error(message)
        Object.assign(error, errorData)
        throw error
      }

      // その他のエラー
      const message = getErrorMessage(errorData)
      const error = new Error(message)
      Object.assign(error, errorData)
      throw error
    }

    // 204 No Content の場合は空のレスポンスを返す
    if (response.status === 204) {
      return {} as T
    }

    const json = await response.json()

    console.groupCollapsed(`📦 Response Data: ${response.status}${response.statusText}`)
    console.log("Data:", json)
    console.groupEnd()

    return json
  } catch (error) {
    console.error("API call error:", error)

    // abortされた場合はそのままエラーを投げる
    if (error instanceof Error && error.name === "AbortError") {
      throw error
    }

    // fetch 自体の失敗（サーバーにつながらない等）
    if (error instanceof TypeError) {
      throw new Error("通信に失敗しました。ネットワーク接続を確認してください。")
    }

    if (error instanceof Error) {
      throw error
    }

    // toast.error('API call failed: Unknown error')
    throw new Error("API call failed: Unknown error")
  }
}

export default customInstance
